import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AlertTriangle,
  Check,
  Eye,
  EyeOff,
  Info,
  Loader2,
  X,
} from 'lucide-react'
import {
  AuthSplitShell,
  AuthStatusPanel,
} from '@/components/auth/AuthSplitShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { authApi } from '@/services/authApi'
import { getApiErrorMessage, tokenStorage } from '@/services/api'
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator'
import { toast } from '@/components/ui/toast'
import {
  passwordMeetsStrength,
  resetPasswordSchema,
  type ResetPasswordFormData,
} from '@/lib/validations/auth'

interface ResetPasswordPageProps {
  mode?: 'reset' | 'setup'
}

export default function ResetPasswordPage({
  mode = 'reset',
}: ResetPasswordPageProps) {
  const { t } = useTranslation('auth')
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const email = params.get('email') ?? ''
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [showPwd, setShowPwd] = useState(false)
  const [showConfirmPwd, setShowConfirmPwd] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
    defaultValues: { password: '', password_confirmation: '' },
  })

  const passwordValue = useWatch({
    control,
    name: 'password',
  })
  const canSubmit = isValid && passwordMeetsStrength(passwordValue ?? '')

  if (!token || !email) {
    return (
      <AuthSplitShell contentWidthClassName='lg:max-w-[61%]'>
        <AuthStatusPanel
          icon={AlertTriangle}
          iconWrapClassName='bg-[#fff0f0]'
          iconClassName='text-destructive'
          title={t('reset.invalidTitle')}
          description={t('reset.invalidDescription')}
        >
          <div className='flex flex-col gap-4'>
            <Button
              className='h-11 w-full rounded-xl border border-[#FF5A1F] bg-[#FF5A1F] px-4 text-base font-medium text-white shadow-[0_12px_24px_rgba(255,90,31,0.2)] transition-transform hover:translate-y-[-1px] hover:bg-[#FF5A1F]/92'
              asChild
            >
              <Link to='/forgot-password'>{t('reset.requestNewLink')}</Link>
            </Button>
            <Button
              variant='ghost'
              className='h-auto min-h-0 w-full rounded-lg px-3 py-1 text-base font-medium leading-none text-[#FF5A1F] hover:bg-transparent hover:text-[#FF5A1F]/80'
              asChild
            >
              <Link to='/login'>{t('reset.backToLogin')}</Link>
            </Button>
          </div>
        </AuthStatusPanel>
      </AuthSplitShell>
    )
  }

  const onSubmit = async (data: ResetPasswordFormData) => {
    setSubmitting(true)
    setServerError(null)
    try {
      if (mode === 'setup') {
        const res = await authApi.setupPassword({
          token,
          password: data.password,
          password_confirmation: data.password_confirmation,
        })
        if (res.data) {
          tokenStorage.setTokens(res.data)
          toast({ title: t('reset.passwordSetSuccess'), variant: 'success' })
          navigate('/dashboard', { replace: true })
          return
        }
      } else {
        await authApi.resetPassword({
          token,
          email,
          password: data.password,
          password_confirmation: data.password_confirmation,
        })
        setResetSuccess(true)
        return
      }
    } catch (err) {
      setServerError(getApiErrorMessage(err, t('reset.genericError')))
    } finally {
      setSubmitting(false)
    }
  }

  if (resetSuccess) {
    return (
      <AuthSplitShell contentWidthClassName='lg:max-w-[61%]'>
        <AuthStatusPanel
          icon={Check}
          iconWrapClassName='bg-[#ecfbf3]'
          iconClassName='text-success'
          title={t('reset.successTitle')}
          description={t('reset.passwordResetSuccess')}
        >
          <Button
            className='h-11 w-full rounded-xl border border-[#FF5A1F] bg-[#FF5A1F] px-4 text-base font-medium text-white shadow-[0_12px_24px_rgba(255,90,31,0.2)] transition-transform hover:translate-y-[-1px] hover:bg-[#FF5A1F]/92'
            asChild
          >
            <Link to='/login'>{t('reset.backToLogin')}</Link>
          </Button>
        </AuthStatusPanel>
      </AuthSplitShell>
    )
  }

  return (
    <AuthSplitShell centerContent={false} rightPaneClassName='py-9 sm:py-10'>
      <div className='space-y-6'>
        <div className='space-y-1.5'>
          <h1 className='font-heading text-[24px] font-semibold leading-[1.2] tracking-[-0.02em] text-[#171A20]'>
            {mode === 'setup' ? t('reset.setTitle') : t('reset.resetTitle')}
          </h1>
          <p className='text-sm leading-6 text-[#3D434E]'>
            {t('reset.description')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className='flex flex-col gap-6'
        >
          {serverError && (
            <div
              role='alert'
              className='relative rounded-2xl border border-[#f4b7ac] bg-[#fff3f0] p-4'
            >
              <div className='flex gap-3'>
                <Info
                  className='mt-0.5 h-5 w-5 shrink-0 text-[#900B09]'
                  aria-hidden
                />
                <div className='flex min-w-0 flex-1 flex-col gap-1 pr-8'>
                  <p className='text-base font-semibold leading-[1.4] text-[#900B09]'>
                    {t('login.errorTitle')}
                  </p>
                  <p className='text-base font-normal leading-[1.4] text-[#900B09]'>
                    {serverError}
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={() => setServerError(null)}
                className='absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-[#900B09] hover:bg-[#EC221F]/10'
                aria-label={t('login.dismissError')}
              >
                <X className='h-4 w-4' />
              </button>
            </div>
          )}

          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <Label
                htmlFor='password'
                className='text-xs font-semibold leading-[1.4] text-[#171A20]'
              >
                {t('reset.newPasswordLabel')}
              </Label>
              <div className='relative'>
                <Input
                  id='password'
                  type={showPwd ? 'text' : 'password'}
                  autoComplete='new-password'
                  maxLength={64}
                  placeholder={t('reset.newPasswordPlaceholder')}
                  aria-invalid={!!errors.password}
                  className={cn(
                    'h-11 rounded-xl border border-transparent bg-[#f5f7fb] px-4 py-3 pr-11 text-sm text-[#171A20] shadow-none placeholder:text-[#3D434E] focus-visible:border-[#FF5A1F]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/12 focus-visible:ring-offset-0',
                    errors.password &&
                      'border-[#EC221F] focus-visible:border-[#EC221F] focus-visible:ring-[#EC221F]/12'
                  )}
                  {...register('password')}
                />
                <button
                  type='button'
                  onClick={() => setShowPwd(s => !s)}
                  className='absolute right-4 top-1/2 -translate-y-1/2 text-[#3D434E] transition-colors hover:text-[#171A20]'
                  aria-label={showPwd ? t('password.hide') : t('password.show')}
                >
                  {showPwd ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className='text-sm font-normal leading-[1.4] text-[#EC221F]'>
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-1'>
              <Label
                htmlFor='password_confirmation'
                className='text-xs font-semibold leading-[1.4] text-[#171A20]'
              >
                {t('reset.confirmPasswordLabel')}
              </Label>
              <div className='relative'>
                <Input
                  id='password_confirmation'
                  type={showConfirmPwd ? 'text' : 'password'}
                  autoComplete='new-password'
                  maxLength={64}
                  placeholder={t('reset.confirmPasswordPlaceholder')}
                  aria-invalid={!!errors.password_confirmation}
                  className={cn(
                    'h-11 rounded-xl border border-transparent bg-[#f5f7fb] px-4 py-3 pr-11 text-sm text-[#171A20] shadow-none placeholder:text-[#3D434E] focus-visible:border-[#FF5A1F]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/12 focus-visible:ring-offset-0',
                    errors.password_confirmation &&
                      'border-[#EC221F] focus-visible:border-[#EC221F] focus-visible:ring-[#EC221F]/12'
                  )}
                  {...register('password_confirmation')}
                />
                <button
                  type='button'
                  onClick={() => setShowConfirmPwd(s => !s)}
                  className='absolute right-4 top-1/2 -translate-y-1/2 text-[#3D434E] transition-colors hover:text-[#171A20]'
                  aria-label={
                    showConfirmPwd ? t('password.hide') : t('password.show')
                  }
                >
                  {showConfirmPwd ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                </button>
              </div>
              {errors.password_confirmation && (
                <p className='text-sm font-normal leading-[1.4] text-[#EC221F]'>
                  {errors.password_confirmation.message}
                </p>
              )}
            </div>

            <PasswordStrengthIndicator />
          </div>

          <div className='flex flex-col gap-4 pt-1'>
            <Button
              type='submit'
              className='h-11 w-full rounded-xl border border-[#FF5A1F] bg-[#FF5A1F] px-4 text-base font-medium text-white shadow-[0_12px_24px_rgba(255,90,31,0.2)] transition-transform hover:translate-y-[-1px] hover:bg-[#FF5A1F]/92'
              disabled={submitting || !canSubmit}
            >
              {submitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {mode === 'setup' ? t('reset.setSubmit') : t('reset.resetSubmit')}
            </Button>

            <Button
              variant='ghost'
              className='h-auto min-h-0 w-full rounded-lg px-3 py-1 text-base font-medium leading-none text-[#FF5A1F] hover:bg-transparent hover:text-[#FF5A1F]/80'
              asChild
            >
              <Link to='/login'>{t('reset.backToLogin')}</Link>
            </Button>
          </div>
        </form>
      </div>
    </AuthSplitShell>
  )
}
